import { Directive, Input, ElementRef, Renderer2, OnDestroy, HostListener } from '@angular/core';

@Directive({
    selector: '[appTooltip]',
    standalone: true
})
export class TooltipDirective implements OnDestroy {
    @Input() public appTooltip: string = '';
    @Input() public tooltipPosition: 'top' | 'bottom' | 'left' | 'right' | 'pointer' = 'right';
    @Input() public tooltipDelay: number = 100;
    @Input() public tooltipDisabled: boolean = false;

    private tooltipElement: HTMLElement | null = null;
    private showTimeout: ReturnType<typeof setTimeout> | null = null;
    private mouseX = 0;
    private mouseY = 0;

    public constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2
    ) {}

    @HostListener('mouseenter')
    public onMouseEnter(): void {
        if (this.tooltipDisabled || !this.appTooltip) {
            return;
        }

        this.showTimeout = setTimeout(() => {
            this.showTooltip();
        }, this.tooltipDelay);
    }

    @HostListener('mousemove', ['$event'])
    public onMouseMove(event: MouseEvent): void {
        if (this.tooltipPosition === 'pointer') {
            this.mouseX = event.clientX;
            this.mouseY = event.clientY;

            if (this.tooltipElement) {
                this.positionTooltip();
            }
        }
    }

    @HostListener('mouseleave')
    public onMouseLeave(): void {
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
        }
        this.hideTooltip();
    }

    private showTooltip(): void {
        if (this.tooltipElement) {
            return;
        }

        // Create tooltip element
        this.tooltipElement = this.renderer.createElement('div') as HTMLElement;
        this.renderer.addClass(this.tooltipElement, 'app-tooltip');
        this.renderer.addClass(this.tooltipElement, `tooltip-${this.tooltipPosition}`);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const text = this.renderer.createText(this.appTooltip);
        this.renderer.appendChild(this.tooltipElement, text);

        // Append to body
        this.renderer.appendChild(document.body, this.tooltipElement);

        // Position tooltip
        this.positionTooltip();
    }

    private positionTooltip(): void {
        if (!this.tooltipElement) {
            return;
        }

        const tooltipPos = this.tooltipElement.getBoundingClientRect();
        const scrollPos = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

        let top = 0;
        let left = 0;

        if (this.tooltipPosition === 'pointer') {
            // Position at pointer with offset
            top = this.mouseY - tooltipPos.height - 10;
            left = this.mouseX + 10;

            // Keep tooltip within viewport
            const viewportWidth = window.innerWidth;

            if (left + tooltipPos.width > viewportWidth) {
                left = this.mouseX - tooltipPos.width - 10;
            }

            if (top < 0) {
                top = this.mouseY + 10;
            }
        } else {
            const hostPos = (this.elementRef.nativeElement as HTMLElement).getBoundingClientRect();

            switch (this.tooltipPosition) {
                case 'right':
                    top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
                    left = hostPos.right + 10;
                    break;
                case 'left':
                    top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
                    left = hostPos.left - tooltipPos.width - 10;
                    break;
                case 'top':
                    top = hostPos.top - tooltipPos.height - 10;
                    left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
                    break;
                case 'bottom':
                    top = hostPos.bottom + 10;
                    left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
                    break;
            }
        }

        this.renderer.setStyle(this.tooltipElement, 'top', `${top + scrollPos}px`);
        this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
    }

    private hideTooltip(): void {
        if (this.tooltipElement) {
            this.renderer.removeChild(document.body, this.tooltipElement);
            this.tooltipElement = null;
        }
    }

    public ngOnDestroy(): void {
        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
        }
        this.hideTooltip();
    }
}
